import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { SERVICES } from "@/lib/site";
import { PageHero, Reveal } from "@/components/common/Reveal";
import { TrustBadges } from "@/components/common/TrustBadges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ServiceBlock = ({ s, i, onLearn }) => (
  <Reveal>
    <article id={s.key} className={`scroll-mt-28 grid md:grid-cols-12 gap-10 py-16 md:py-20 border-t border-white/10 ${i % 2 ? "md:text-right" : ""}`} data-testid={`service-block-${s.key}`}>
      <div className={`md:col-span-5 ${i % 2 ? "md:order-2" : ""}`}>
        <span className="font-display text-7xl md:text-8xl text-[#D4AF37]/25">0{i + 1}</span>
        <h2 className="mt-2 text-4xl sm:text-5xl font-medium">{s.title}</h2>
        <p className="mt-4 font-display italic text-xl text-[#D4AF37]">{s.tagline}</p>
      </div>
      <div className={`md:col-span-7 lux-card p-8 md:p-10 text-left ${i % 2 ? "md:order-1" : ""}`}>
        <s.icon className="w-9 h-9 text-[#D4AF37]" strokeWidth={1.2} />
        <p className="mt-6 text-white/75 font-light leading-relaxed text-base md:text-lg">{s.summary}</p>
        <ul className="mt-8 grid sm:grid-cols-2 gap-4">
          {s.items.map((it) => (
            <li key={it} className="flex gap-3 text-sm text-white/85"><Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />{it}</li>
          ))}
        </ul>
        <div className="mt-10 flex flex-wrap gap-4">
          <button onClick={() => onLearn(s)} className="btn-outline-gold" data-testid={`service-learn-more-${s.key}`}>Learn More</button>
          <Link to={`/book?service=${encodeURIComponent(s.title)}`} className="btn-gold" data-testid={`service-request-consultation-${s.key}`}>Request Consultation</Link>
        </div>
      </div>
    </article>
  </Reveal>
);

export default function Services() {
  const [active, setActive] = useState(null);
  return (
    <div data-testid="services-page">
      <PageHero testid="services-hero" eyebrow="Our Services" title={<>Strategic solutions for every stage of your <span className="italic gold-text">financial journey.</span></>}
        sub="From filing season to founding your company, B2R brings strategy, education and exceptional service to every engagement." />
      <section className="bg-[#0A0A0A] pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {SERVICES.map((s, i) => <ServiceBlock key={s.key} s={s} i={i} onLearn={setActive} />)}
          <div className="pt-16 border-t border-white/10"><TrustBadges /></div>
        </div>
      </section>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#14051C] border-[#D4AF37]/30 max-w-lg w-[calc(100%-2rem)] max-h-[90svh] overflow-y-auto rounded-2xl" data-testid="service-learn-more-dialog">
          {active && (
            <>
              <DialogHeader>
                <p className="eyebrow">Service Details</p>
                <DialogTitle className="font-display text-3xl font-medium">{active.title}</DialogTitle>
                <DialogDescription className="text-white/70 leading-relaxed pt-2">{active.summary}</DialogDescription>
              </DialogHeader>
              <ul className="space-y-3 my-2">
                {active.items.map((it) => <li key={it} className="flex gap-3 text-sm"><Check className="w-4 h-4 text-[#D4AF37]" />{it}</li>)}
              </ul>
              <p className="text-sm text-white/55">Every plan begins with a consultation so we can tailor our strategy to your unique situation.</p>
              <Link to={`/book?service=${encodeURIComponent(active.title)}`} className="btn-gold mt-2" data-testid="dialog-request-consultation">Request Consultation</Link>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
